import {
  AFFINITY_TYPES,
  ATTUNE,
  CARVE,
  RES_TYPE,
  isResAttr,
  CATCH_UP,
  GEAR_EFFECT_FX,
  GEAR_STACK_BP,
  UNIQUE_EFFECT_BP,
  GEM_ATTR,
  GEMS,
  OFFERINGS,
  OFFERING_KINDS,
  RELIC_AWAKEN,
  SOCKET_OPEN,
  DIRECTED_REFORGE,
  MASTERWORK,
  GEM_CUT_BP,
  GEM_CUT_KEYS,
  type GemCut,
  TEMPER,
  type GearEffect,
  type GemId,
  type OfferingKind,
  type RelicId,
  attrValue,
  AWAKEN,
  WORN_SLOTS,
  canWearBase,
  PANOPLY,
  UNIQUES,
  type UniqueSlot,
} from '@somnhold/content';
import { featNeed } from './feat-block.ts';
import { infusable, infusedBp, awakenBlock, awakenFuel, sealAsked, sealSpent, behindGroup, forgeLevel, itemAttrs, itemBudget, itemSockets, nextAwaken, socketMax, wornSlotFor, offeringCost, offeringsFor, reforgeChoices, reforgeCost, type CampaignCommand, type CampaignState, type ItemInstance, type PlayerState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { flashItem } from './panels.ts';
import { getLang, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/**
 * The armory (2026-10-25): what a rare effect does, with its numbers; the obsidian setting and its gem;
 * the forge's deeper work (reforge, settings, temper, carve) folded behind one button per piece; relics
 * that awaken; the keep's offerings; and the catch-up badge.
 */

type Cmd = (c: CampaignCommand) => Promise<boolean>;

export const EFFECT_ICON: Record<GearEffect, string> = {
  burn: ICONS.fireball,
  lifesteal: ICONS.drop,
  thorns: ICONS.thornWall,
  swift: ICONS.walk,
  fury: ICONS.frenzy,
  execute: ICONS.executioner,
  chain: ICONS.haste,
  frost: ICONS.frostWall,
  cleave: ICONS.cleave,
  guard: ICONS.shield,
  echo: ICONS.hourglass,
  banner: ICONS.banner,
};

/** Each gem's colour (no gold: rubies, wine, sapphire, violet and black glass). */
export const GEM_COLOR: Record<GemId, string> = { ruby: '#ff4f3a', garnet: '#c03a5a', sapphire: '#3a8bff', amethyst: '#b05cff', onyx: '#9aa1ad', emerald: '#3fd6a0' };

export const OFFERING_ICON: Record<OfferingKind, string> = { oil: ICONS.oil, mortar: ICONS.mortar, vigil: ICONS.vigil };

const pct = (bp: number): number => Math.round(bp / 100);
/** Seconds from ticks (12 a second), with a decimal comma in Spanish. */
const secs = (ticks: number): string => (Math.round((ticks / 12) * 10) / 10).toLocaleString(getLang() === 'es' ? 'es-AR' : 'en-US');

/** What a rare effect does, with its numbers from the content (at full strength, or at `bp` of it: a unique piece's own). */
export const effectDesc = (e: GearEffect, bp = 10000): string => {
  const f = GEAR_EFFECT_FX;
  const k = (v: number): number => pct(Math.trunc((v * bp) / 10000));
  switch (e) {
    case 'burn':
      return t('effect.burn.desc', { bp: k(f.burn.bp), s: secs(f.burn.ticks) });
    case 'lifesteal':
      return t('effect.lifesteal.desc', { bp: k(f.lifesteal.bp) });
    case 'thorns':
      return t('effect.thorns.desc', { bp: k(f.thorns.attackBp) });
    case 'swift':
      return t('effect.swift.desc', { bp: k(f.swift.bp) });
    case 'fury':
      return t('effect.fury.desc', { bp: k(f.fury.bp), below: pct(f.fury.belowBp) });
    case 'execute':
      return t('effect.execute.desc', { bp: k(f.execute.bp), below: pct(f.execute.belowBp) });
    case 'chain':
      return t('effect.chain.desc', { bp: k(f.chain.bp), every: f.chain.every, n: f.chain.targets });
    case 'frost':
      return t('effect.frost.desc', { bp: k(f.frost.bp), s: secs(f.frost.ticks) });
    case 'cleave':
      return t('effect.cleave.desc', { bp: k(f.cleave.bp) });
    case 'guard':
      return t('effect.guard.desc', { bp: k(f.guard.bp), every: secs(f.guard.every), s: secs(f.guard.ticks) });
    case 'echo':
      return t('effect.echo.desc', { s: secs(Math.trunc((f.echo.ticks * bp) / 10000)) });
    case 'banner':
      return t('effect.banner.desc', { bp: k(f.banner.bp) });
  }
};

/** How strong a piece's effect is of a rare piece's (bp): a unique's own is stronger; otherwise its slot's budget (two hands 150 %, the small slots half). */
export const effectStrength = (item: ItemInstance): number =>
  item.unique ? (item.infused ? infusedBp(item.infused.rank) : UNIQUE_EFFECT_BP) + (item.awaken ?? 0) * AWAKEN.effectStepBp : itemBudget(item);

/**
 * How much of its effect a piece gives where it would be worn (bp): whole if no other piece in the
 * other slots has it, less for each one before it (the sim adds them weapon, armor, amulet).
 */
export const effectShare = (p: PlayerState, item: ItemInstance): number => {
  if (!item.effect) return 0;
  let before = 0;
  const at = wornSlotFor(p.hero, item);
  for (const slot of WORN_SLOTS) {
    if (slot === at) break;
    if (p.hero.gear[slot]?.effect === item.effect && p.hero.gear[slot]?.id !== item.id) before++;
  }
  // A piece in the bag would sit in its slot: the ones after it count only if worn already ahead.
  return GEAR_STACK_BP[before] ?? 0;
};

/** The effect's line on a card: its glyph, its name and what it does (and what it's worth if repeated). */
export const effectBlock = (p: PlayerState, item: ItemInstance): HTMLElement | '' => {
  // A unique slot's infused effect takes the place of the piece's own (docs/PANOPLIA.md §6): it shows, with its rank, and the own one struck under it.
  const inf = item.unique ? item.infused : undefined;
  const fx = inf ? inf.effect : item.effect;
  if (!fx) return '';
  const share = effectShare(p, { ...item, effect: fx });
  return h(
    `div.fx-line${inf ? '.infused' : ''}`,
    { 'data-effect': fx, ...(inf ? { 'data-test': 'infused' } : {}) },
    h('span.fx-ico', { html: EFFECT_ICON[fx] }),
    h(
      'span.fx-txt',
      {},
      h('b', {}, t(`effect.${fx}`)),
      inf ? h('span.fx-inf', {}, ` · ${t('infuse.badge', { n: inf.rank, m: PANOPLY.infuse.ranks })}`) : '',
      ' — ',
      effectDesc(fx, effectStrength(item)),
      share < 10000 ? h('span.fx-stack', { title: t('effect.stackHint') }, ` ${t('effect.stacked', { n: pct(share) })}`) : '',
      inf && item.effect ? h('s.fx-own', { title: t('infuse.ownHint') }, ` ${t(`effect.${item.effect}`)}`) : '',
    ),
  );
};

/** Infusing (docs/PANOPLIA.md §6): the rares of the slot that can be burnt, what each would leave, and two touches to do it. */
const infuseSection = (s: CampaignState, p: PlayerState, item: ItemInstance, run: (c: CampaignCommand) => Promise<void>, rerender: () => void): HTMLElement => {
  const dust = s.storage.dust;
  const price = PANOPLY.infuse.dust;
  const rares = infusable(p.hero, item.slot as UniqueSlot);
  const now = item.infused;
  const sec = h('section.forge-sec.infuse-sec', { 'data-test': 'infuse' }, h('h5', {}, t('infuse.title'), ' ', chip(ICONS.dust, price, dust < price)), h('p.small', {}, t('infuse.hint', { n: PANOPLY.infuse.ranks })));
  if (!rares.length) sec.append(h('p.small.hint', {}, t('infuse.none')));
  for (const r of rares) {
    const rank = now?.effect === r.effect ? now.rank + 1 : 0;
    const full = now?.effect === r.effect && now.rank >= PANOPLY.infuse.ranks;
    const armed = drawer.infuse === r.id;
    sec.append(
      h(
        `div.infuse-row${armed ? '.armed' : ''}`,
        { 'data-rare': String(r.id) },
        h('span.fx-ico', { html: EFFECT_ICON[r.effect as NonNullable<ItemInstance['effect']>] }),
        h('span.small', {}, h('b', {}, t(`effect.${r.effect}`)), ' · ', full ? t('infuse.full') : now?.effect === r.effect ? t('infuse.rankUp', { n: rank }) : t('infuse.replace', { was: t(`effect.${now?.effect ?? item.effect ?? 'cleave'}`) })),
        h(
          `button.btn.small${armed ? '.danger' : '.stone'}`,
          {
            'data-test': armed ? 'infuse-confirm' : 'infuse-go',
            disabled: full || dust < price,
            onclick: () => {
              if (!armed) {
                drawer.infuse = r.id;
                rerender();
                return;
              }
              drawer.infuse = 0;
              void run({ t: 'infuse', item: item.id, rare: r.id });
            },
          },
          armed ? t('infuse.confirm') : t('infuse.go'),
        ),
      ),
    );
  }
  if (now) sec.append(h('button.btn.small.ghost', { 'data-test': 'uninfuse', disabled: dust < price, onclick: () => void run({ t: 'uninfuse', item: item.id }) }, t('infuse.back')));
  return sec;
};

/** A gem's name with its cut (the cut as «talla …», so Spanish needn't agree in gender). */
export const gemName = (gem: GemId, cut: GemCut): string => t('gem.withCut', { gem: t(`gem.${gem}`), cut: t(`gemCut.${GEM_CUT_KEYS[cut]}`) });

/** What a gem adds set in this piece: its cut's value times the slot's budget (certeza as critical chance). */
const gemAdds = (item: ItemInstance, gem: GemId, cut: GemCut): string => {
  const k = GEM_ATTR[gem];
  const bp = Math.trunc(((GEM_CUT_BP[cut] as number) * itemBudget(item)) / 10000);
  return t(`attr.${k}`, { v: Math.round(attrValue(k, bp) / 10) / 10 });
};

/** A piece's settings on its card: each gem with its cut and what it adds, or that it waits for one. */
export const settingBlock = (item: ItemInstance): HTMLElement | '' => {
  const all = itemSockets(item);
  if (!all.length) return '';
  return h(
    'div.settings',
    {},
    ...all.map((g) =>
      h(
        `div.setting-line${g ? '' : '.empty'}`,
        { style: g ? `--gem:${GEM_COLOR[g.gem]}` : '' },
        h('span.gem-ico', { html: g ? ICONS.gem : ICONS.setting }),
        g ? h('span', {}, h('b', {}, gemName(g.gem, g.cut)), ' · ', gemAdds(item, g.gem, g.cut)) : h('span', {}, t('socket.empty')),
      ),
    ),
  );
};

/** From what share of the common dust a reforge's price gets a warning. */
const REFORGE_WARN_BP = 2000;

const chip = (icon: string, n: number, short: boolean): HTMLElement => h(`span.cost-chip${short ? '.short' : ''}`, {}, h('span', { html: icon }), String(n));

/**
 * Awakening a unique piece (docs/PROGRESION.md): what the next stage gives and asks, each need ticked or not, and the
 * rares that would be melted into it (the worst in the bag). Once every stage is awake, it says so.
 */
const awakenSection = (s: CampaignState, p: PlayerState, item: ItemInstance, run: (c: CampaignCommand) => Promise<void>): HTMLElement => {
  const st = nextAwaken(item);
  const stage = item.awaken ?? 0;
  const sec = h('section.forge-sec.awaken-sec', { 'data-test': 'awaken' }, h('h5', {}, t('uawaken.title'), stage ? h('span.awaken-now', {}, ` · ${t('uawaken.stage', { n: ['', 'I', 'II', 'III', 'IV'][stage] ?? '' })}`) : ''));
  if (!st) {
    sec.append(h('p.small.ok', {}, t('uawaken.done')));
    return sec;
  }
  const fuel = awakenFuel(p).slice(0, st.rares);
  // An ember of the dragon's heart (La Orilla 4) pays the dust and the rares.
  const embers = p.hero.dragonEmbers ?? 0;
  const ember = embers > 0;
  const seals = p.hero.seals?.[st.seal] ?? 0;
  const need = (ok: boolean, text: string): HTMLElement => h(`li${ok ? '.ok' : '.no'}`, {}, h('span.mark', { 'aria-hidden': 'true' }, ok ? '✓' : '✗'), text);
  // The seal: no amulet asks those of stages I to III; the dragon's is a permit, never spent (docs/UNICAS.md §2.1).
  const sealLine = !sealAsked(item) ? '' : need(seals > 0, t(sealSpent(item) ? 'uawaken.needSeal' : 'uawaken.sealKept', { s: t(`seal.${st.seal}`), n: seals }));
  sec.append(
    h('p.small', {}, t('uawaken.gives', { a: pct(AWAKEN.attrStepBp), e: Math.round((AWAKEN.effectStepBp * 100) / 10000), p: Math.round((AWAKEN.powerStepBp * 100) / 10000) })),
    h(
      'ul.awaken-needs',
      {},
      // The stage's feat first (docs/UNICAS.md §6): grey with what is left until it is done.
      featNeed(s, p.hero, item),
      need(p.hero.level >= st.level, t('uawaken.needLevel', { n: st.level, m: p.hero.level })),
      need(forgeLevel(s) >= st.forge, t('uawaken.needForge', { n: st.forge, m: forgeLevel(s) })),
      sealLine,
      ember ? need(true, t('uawaken.ember', { n: embers })) : need(s.storage.dust >= st.dust, t('uawaken.needDust', { n: st.dust, m: s.storage.dust })),
      ember ? '' : need(fuel.length >= st.rares, t('uawaken.needRares', { n: st.rares, m: awakenFuel(p).length })),
    ),
  );
  const ok = awakenBlock(s, p, item) === null && (ember || fuel.length >= st.rares);
  sec.append(
    h('button.btn.small.awaken-go', { 'data-test': 'awaken-go', disabled: !ok, onclick: () => { flashItem(item.id); void run({ t: 'awakenUnique', item: item.id, fuel: ember ? [] : fuel.map((x) => x.id) }); } }, t('uawaken.go', { n: ['', 'I', 'II', 'III', 'IV'][stage + 1] ?? '' })),
    ok ? h('p.small.hint', {}, ember ? t('uawaken.emberUses') : t('uawaken.melts', { n: st.rares })) : '',
  );
  return sec;
};

/** Which setting lists the pouch's gems (one at a time, so a phone isn't a wall of gems). */
const gemPick = { item: 0, at: -1 };
/** Which piece's forge drawer is open, and which attribute of a unique piece is being reforged (they survive the sheet re-rendering). */
const drawer = { open: 0, from: '', infuse: 0 };

/**
 * The forge's deeper work on one piece, folded behind a button: reforge an attribute, open a setting
 * and set a gem, temper, carve. Each control shows its price and is greyed out when it can't be paid.
 */
export const forgeDrawer = (s: CampaignState, p: PlayerState, item: ItemInstance, cmd: Cmd, rerender: () => void): HTMLElement | '' => {
  const open = drawer.open === item.id;
  const toggle = h(
    `button.btn.small.ghost.forge-toggle${open ? '.on' : ''}`,
    { 'aria-expanded': String(open), 'data-test': 'forge-more', onclick: () => { drawer.open = open ? 0 : item.id; rerender(); } },
    h('span', { html: ICONS.hammer }),
    t('forge.more'),
    h('span.chev', { 'aria-hidden': 'true' }, open ? '▴' : '▾'),
  );
  if (!open) return h('div.forge-drawer', {}, toggle);
  const dust = s.storage.dust;
  const hero = p.hero;
  const run = async (c: CampaignCommand): Promise<void> => {
    if (await cmd(c)) {
      audio.play('build');
      rerender();
    }
  };
  // Everything here is paid from the storage everyone fills: say so, and how much is in it.
  const body = h('div.forge-body', {}, h('p.small.forge-pool', { 'data-test': 'forge-pool' }, h('span', { html: ICONS.dust }), t('forge.pool', { n: dust })));
  // Reforge: one button per attribute the piece has. Its price grows each time, so show the next one too.
  const cost = reforgeCost(item);
  const next = reforgeCost({ ...item, reforged: (item.reforged ?? 0) + 1 });
  const attrs = Object.keys(item.attrs).sort() as (keyof ItemInstance['attrs'])[];
  const shown = itemAttrs(item);
  if (item.unique) {
    body.append(awakenSection(s, p, item, run));
    if (UNIQUES[item.unique].cls === hero.heroClass) body.append(infuseSection(s, p, item, run, rerender));
    // A unique piece: pick the attribute, then what it becomes; the value stays.
    const from = attrs.find((k) => k === drawer.from);
    const pickFrom = (k: string): void => {
      drawer.from = drawer.from === k ? '' : k;
      rerender();
    };
    body.append(
      h(
        'section.forge-sec',
        {},
        h('h5', {}, t('reforge.title'), ' ', chip(ICONS.dust, cost, dust < cost)),
        h('p.small.reforge-price', { 'data-test': 'reforge-price' }, t('reforge.price', { now: cost, next, add: next - cost })),
        h('p.small', {}, t('reforge.uniqueHint')),
        h('div.forge-row', {}, ...attrs.map((k) => h(`button.btn.small.stone${from === k ? '.on' : ''}`, { 'data-test': 'reforge-from', 'aria-pressed': String(from === k), onclick: () => pickFrom(k) }, t(`attr.${k}`, { v: pct(attrValue(k, shown[k] ?? 0)) })))),
        from
          ? h(
              'div.forge-row',
              {},
              h('span.small', {}, t('reforge.into')),
              ...reforgeChoices(item, from).map((k) =>
                h('button.btn.small.stone', { 'data-test': 'reforge', disabled: dust < cost, onclick: () => { drawer.from = ''; void run({ t: 'reforge', item: item.id, attr: from, to: k }); } }, t(`attr.${k}`, { v: pct(attrValue(k, item.attrs[from] ?? 0)) })),
              ),
            )
          : '',
        dust >= cost ? h('p.small.reforge-after', { 'data-test': 'reforge-after' }, t('reforge.after', { n: dust - cost })) : '',
      ),
    );
    return h('div.forge-drawer.open', {}, toggle, body);
  }
  if (attrs.length < 5) {
    // A big bite of the common dust gets a warning, before anyone taps.
    const share = dust > 0 ? Math.round((cost * 100) / dust) : 0;
    const big = dust >= cost && cost * 10000 >= dust * REFORGE_WARN_BP;
    body.append(
      h(
        'section.forge-sec',
        {},
        h('h5', {}, t('reforge.title'), ' ', chip(ICONS.dust, cost, dust < cost)),
        h('p.small.reforge-price', { 'data-test': 'reforge-price' }, t('reforge.price', { now: cost, next, add: next - cost })),
        h('p.small', {}, t('reforge.hint')),
        h('div.forge-row', {}, ...attrs.map((k) => h('button.btn.small.stone', { 'data-test': 'reforge', disabled: dust < cost, onclick: () => void run({ t: 'reforge', item: item.id, attr: k }) }, t('reforge.go', { a: t(`attr.${k}`, { v: pct(attrValue(k, shown[k] ?? 0)) }) })))),
        dust >= cost ? h(`p.small.reforge-after${big ? '.big' : ''}`, { 'data-test': 'reforge-after' }, big ? t('reforge.big', { p: share, n: dust - cost }) : t('reforge.after', { n: dust - cost })) : '',
      ),
    );
    // Directed reforging (level 38): pick the attribute, then what it becomes, rolled anew, at triple the dust.
    if (hero.level >= DIRECTED_REFORGE.level) {
      const dcost = Math.trunc((cost * DIRECTED_REFORGE.costBp) / 10000);
      const from = attrs.find((k) => k === drawer.from);
      body.append(
        h(
          'section.forge-sec',
          { 'data-test': 'directed' },
          h('h5', {}, t('reforge.directedTitle'), ' ', chip(ICONS.dust, dcost, dust < dcost)),
          h('p.small', {}, t('reforge.directedHint')),
          h('div.forge-row', {}, ...attrs.map((k) => h(`button.btn.small.stone${from === k ? '.on' : ''}`, { 'data-test': 'directed-from', 'aria-pressed': String(from === k), onclick: () => { drawer.from = drawer.from === k ? '' : k; rerender(); } }, t(`attr.${k}`, { v: pct(attrValue(k, shown[k] ?? 0)) })))),
          from
            ? h(
                'div.forge-row',
                {},
                h('span.small', {}, t('reforge.into')),
                ...reforgeChoices(item, from).map((k) => h('button.btn.small.stone', { 'data-test': 'directed-to', disabled: dust < dcost, onclick: () => { drawer.from = ''; void run({ t: 'reforge', item: item.id, attr: from, to: k }); } }, t(`attrName.${k}`))),
              )
            : '',
        ),
      );
    }
  }
  // A masterwork (level 48, once per hero): a very rare piece's attributes all at the top of their range.
  if (item.rarity === 'veryRare') {
    const used = hero.masterwork !== undefined;
    const ready = hero.level >= MASTERWORK.level;
    const can = ready && !used && !item.masterwork && dust >= MASTERWORK.dust && (hero.embers ?? 0) >= MASTERWORK.embers && (hero.shards ?? 0) >= MASTERWORK.shards;
    body.append(
      h(
        'section.forge-sec',
        { 'data-test': 'masterwork' },
        h('h5', {}, t('masterwork.title')),
        h('p.small', {}, item.masterwork ? t('masterwork.done') : used ? t('masterwork.used') : ready ? t('masterwork.hint') : t('masterwork.level', { n: MASTERWORK.level })),
        !item.masterwork && !used && ready
          ? h('div.forge-row', {}, h('button.btn.small.stone', { 'data-test': 'masterwork-go', disabled: !can, onclick: () => void run({ t: 'masterwork', item: item.id }) }, t('masterwork.go'), ' ', chip(ICONS.ember, MASTERWORK.embers, (hero.embers ?? 0) < MASTERWORK.embers), chip(ICONS.shard, MASTERWORK.shards, (hero.shards ?? 0) < MASTERWORK.shards), chip(ICONS.dust, MASTERWORK.dust, dust < MASTERWORK.dust)))
          : '',
      ),
    );
  }
  // An affinity: carve it into another damage type, its value kept (to answer what the omen says is coming).
  const aff = attrs.find(isResAttr);
  if (aff) {
    const was = RES_TYPE[aff];
    const price = ATTUNE.dust[item.rarity];
    body.append(
      h(
        'section.forge-sec',
        {},
        h('h5', {}, t('attune.title'), ' ', chip(ICONS.dust, price, dust < price)),
        h('p.small', {}, t('attune.hint', { from: t(`dtype.${was}`) })),
        h(
          'div.forge-row',
          {},
          ...AFFINITY_TYPES.filter((k) => k !== was).map((k) =>
            h(`button.btn.small.stone.dt-${k}`, { 'data-test': 'attune', 'data-type': k, disabled: dust < price, onclick: () => void run({ t: 'attune', item: item.id, to: k }) }, t(`dtype.${k}`)),
          ),
        ),
      ),
    );
  }
  // Settings (docs/EQUIPO.md §5): a gem from the pouch into each, out again whole, and the next one to open.
  {
    const all = itemSockets(item);
    const max = socketMax(item);
    const sec = h('section.forge-sec', { 'data-test': 'sockets' }, h('h5', {}, h('span.h5-ico', { html: ICONS.setting }), t('socket.label'), h('span.small', {}, ` ${all.length} / ${max}`)));
    const owned = Object.entries(hero.gems ?? {})
      .filter(([, n]) => n > 0)
      .map(([k, n]) => {
        const [gem, cut] = k.split(':');
        return { gem: gem as GemId, cut: Number(cut) as GemCut, n };
      })
      .sort((a, b) => GEMS.indexOf(a.gem) - GEMS.indexOf(b.gem) || b.cut - a.cut);
    all.forEach((g, at) => {
      const row = h('div.socket-row', { 'data-socket': String(at) });
      if (g) {
        row.append(
          h('span.setting-line', { style: `--gem:${GEM_COLOR[g.gem]}` }, h('span.gem-ico', { html: ICONS.gem }), h('span', {}, h('b', {}, gemName(g.gem, g.cut)), ' · ', gemAdds(item, g.gem, g.cut))),
          h('button.btn.small.ghost', { 'data-test': 'unset-gem', onclick: () => void run({ t: 'unsetGem', item: item.id, at }) }, t('socket.take')),
        );
      } else row.append(h('span.setting-line.empty', {}, h('span.gem-ico', { html: ICONS.setting }), h('span', {}, t('socket.empty'))));
      // What the pouch holds can go in (or in place of the gem there): listed for the setting picked, one at a time.
      const choices = owned.filter((o) => !g || o.gem !== g.gem || o.cut !== g.cut);
      const open = gemPick.item === item.id && gemPick.at === at;
      if (choices.length)
        row.append(
          h(
            `button.btn.small.ghost.pick-gem${open ? '.on' : ''}`,
            { 'data-test': 'pick-gem', 'aria-expanded': String(open), onclick: () => { gemPick.item = open ? 0 : item.id; gemPick.at = open ? -1 : at; rerender(); } },
            g ? t('socket.change') : t('socket.pick'),
          ),
        );
      if (choices.length && open)
        row.append(
          h(
            'div.gem-pick',
            {},
            ...choices.map((o) =>
              h(
                'button.gem-btn',
                { style: `--gem:${GEM_COLOR[o.gem]}`, 'data-gem': o.gem, 'data-cut': String(o.cut), 'data-test': 'gem', onclick: () => void run({ t: 'setGem', item: item.id, at, gem: o.gem, cut: o.cut }) },
                h('span.gem-ico', { html: ICONS.gem }),
                h('b', {}, gemName(o.gem, o.cut)),
                h('span.small', {}, `${gemAdds(item, o.gem, o.cut)} · ×${o.n}`),
              ),
            ),
          ),
        );
      sec.append(row);
    });
    if (!owned.length) sec.append(h('p.small.hint', {}, t('socket.noGems')));
    const next = SOCKET_OPEN[all.length];
    if (all.length >= max || !next) sec.append(h('p.small.hint', {}, t('socket.max', { n: max })));
    else if (hero.level < next.level) sec.append(h('p.small.hint', {}, t('socket.openLevel', { n: all.length + 1, l: next.level })));
    else {
      const can = dust >= next.dust && (hero.embers ?? 0) >= next.embers && (hero.shards ?? 0) >= next.shards;
      sec.append(
        h(
          'div.forge-row',
          {},
          h(
            'button.btn.small.stone',
            { 'data-test': 'socket', disabled: !can, onclick: () => void run({ t: 'socket', item: item.id }) },
            t('socket.openN', { n: all.length + 1 }),
            ' ',
            next.embers ? chip(ICONS.ember, next.embers, (hero.embers ?? 0) < next.embers) : '',
            next.shards ? chip(ICONS.shard, next.shards, (hero.shards ?? 0) < next.shards) : '',
            chip(ICONS.dust, next.dust, dust < next.dust),
          ),
        ),
      );
    }
    body.append(sec);
  }
  // Temper (embers) and carve (shards), as before.
  const more = h('div.forge-row');
  if (item.rarity === 'common' || item.rarity === 'uncommon') {
    const to = item.rarity === 'common' ? 'uncommon' : 'rare';
    const c = TEMPER[to];
    const can = (hero.embers ?? 0) >= c.embers && dust >= c.dust;
    more.append(h('button.btn.small.temper', { 'data-test': 'temper', disabled: !can, title: t('temper.hint', { r: t(`rarity.${to}`) }), onclick: () => void run({ t: 'temper', item: item.id }) }, t('temper.go', { r: t(`rarity.${to}`) }), ' ', chip(ICONS.ember, c.embers, (hero.embers ?? 0) < c.embers), chip(ICONS.dust, c.dust, dust < c.dust)));
  } else if (item.effect && !item.unique) {
    const can = (hero.shards ?? 0) >= CARVE.shards && dust >= CARVE.dust;
    more.append(h('button.btn.small.carve', { 'data-test': 'carve', disabled: !can, title: t('carve.hint', { fx: t(`effect.${item.effect}`) }), onclick: () => void run({ t: 'carve', item: item.id }) }, t('carve.go'), ' ', chip(ICONS.shard, CARVE.shards, (hero.shards ?? 0) < CARVE.shards), chip(ICONS.dust, CARVE.dust, dust < CARVE.dust)));
  }
  body.append(h('section.forge-sec', {}, more));
  return h('div.forge-drawer.open', {}, toggle, body);
};

/** A relic's card: carried or kept, awake or not, and the forge's offer to wake it. */
export const relicCard = (s: CampaignState, p: PlayerState, r: RelicId, opts: { kept: boolean; forge: boolean; cmd: Cmd; rerender: () => void; extra?: HTMLElement | '' }): HTMLElement => {
  const hero = p.hero;
  const awake = (hero.awakened ?? []).includes(r);
  const body = h('div', {}, h('b', {}, t(`relic.${r}`), awake ? h('span.awake-badge', {}, h('span', { html: ICONS.sparkle }), t('awaken.badge')) : ''), h('div.small', {}, t(`relic.${r}.desc`)));
  if (awake) body.append(h('div.small.awake-fx', {}, t('awaken.then', { d: t(`relic.${r}.awake`) })));
  else if (opts.forge) {
    const c = RELIC_AWAKEN;
    const sh = (hero.shards ?? 0) < c.shards;
    const em = (hero.embers ?? 0) < c.embers;
    const du = s.storage.dust < c.dust;
    body.append(
      h('div.small.awake-next', {}, t('awaken.then', { d: t(`relic.${r}.awake`) })),
      h(
        'div.relic-swap',
        {},
        h(
          'button.btn.small.stone.awaken',
          {
            'data-test': 'awaken',
            title: t('awaken.hint'),
            disabled: sh || em || du,
            onclick: async () => {
              if (await opts.cmd({ t: 'awaken', relic: r })) {
                audio.play('horn', 0.5);
                opts.rerender();
              }
            },
          },
          h('span', { html: ICONS.sparkle }),
          t('awaken.go'),
          ' ',
          chip(ICONS.shard, c.shards, sh),
          chip(ICONS.ember, c.embers, em),
          chip(ICONS.dust, c.dust, du),
        ),
      ),
    );
  }
  if (opts.extra) body.append(opts.extra);
  return h(`div.relic.has${opts.kept ? '.kept' : ''}${awake ? '.awake' : ''}`, { title: t(`relic.${r}.desc`), 'data-relic': r }, h('span.relic-ico', { html: ICONS[r] }), body);
};

/** «Keeping pace · ×2 XP» under the XP bar, while the hero lags the group. */
export const paceBadge = (s: CampaignState, p: PlayerState): HTMLElement | '' =>
  behindGroup(s, p.id) ? h('div.pace-badge', { title: t('pace.tip', { n: CATCH_UP.belowBy }), 'data-test': 'pace' }, h('span', { html: ICONS.sparkle }), t('pace.badge')) : '';

const RANK: Record<string, number> = { common: 1, uncommon: 2, rare: 3, veryRare: 4, unique: 5 };
const worth = (it: ItemInstance | undefined): number => (it ? RANK[it.rarity]! * 100000 + Object.values(itemAttrs(it)).reduce((a: number, v) => a + (v ?? 0), 0) : 0);

/** Why a piece would help a friend: nothing in that slot, or better than what they wear. */
export const giftNeed = (friend: PlayerState, item: ItemInstance): string => {
  // A piece of a base their class doesn't wear helps them not at all.
  if (item.base && !canWearBase(friend.hero.heroClass, item.base)) return '';
  const worn = friend.hero.gear[wornSlotFor(friend.hero, item)];
  if (!worn) return t('gift.needEmpty', { slot: t(`slot.${item.slot}`).toLowerCase() });
  return worth(item) > worth(worn) ? t('gift.needBetter') : '';
};

/** The keep's offerings: what each blesses, what is lit this Marea and the price of the next. */
export const offeringsBox = (s: CampaignState, cmd: Cmd): HTMLElement => {
  const lit = offeringsFor(s, s.mareaIndex);
  const dust = offeringCost(s);
  const box = h('section.offerings', { 'data-test': 'offerings' }, h('h4', {}, t('offering.title')), h('p.small.hint', {}, t('offering.hint')));
  const n = (k: OfferingKind): number => (k === 'oil' ? pct(OFFERINGS.oil.towerDamageBp) : k === 'mortar' ? 100 - pct(OFFERINGS.mortar.structureTakenBp) : pct(OFFERINGS.vigil.ghostPowerBp));
  const row = h('div.offer-row');
  for (const k of OFFERING_KINDS) {
    const on = lit.includes(k);
    row.append(
      h(
        `button.offer${on ? '.lit' : ''}`,
        {
          'data-offering': k,
          disabled: on || s.status !== 'active' || s.storage.dust < dust,
          'aria-pressed': String(on),
          onclick: async () => {
            if (await cmd({ t: 'offering', kind: k })) audio.play('horn', 0.4);
          },
        },
        h('span.offer-ico', { html: OFFERING_ICON[k] }),
        h('b', {}, t(`offering.${k}`)),
        h('span.small', {}, t(`offering.${k}.desc`, { n: n(k) })),
        on ? h('span.small.offer-lit', {}, t('offering.lit', { m: s.mareaIndex + 1 })) : h('span.offer-cost', {}, t('offering.burn'), ' ', chip(ICONS.dust, dust, s.storage.dust < dust)),
      ),
    );
  }
  box.append(row);
  return box;
};
